import React, { useEffect, useState } from 'react';
import { eventAPI } from '../../lib/eventApi';
import MovementBadge from './MovementBadge';

export default function StandingsPanel({ sessionId, refreshKey = 0, highlightProfileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    eventAPI.getStandings(sessionId)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sessionId, refreshKey]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="standings-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-[#f0ece3] rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }
  if (!data || !data.standings || data.standings.length === 0) {
    return (
      <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-center" data-testid="standings-empty">
        <p className="text-sm text-[#6a766e]">Standings will appear once Week 1 is finalized.</p>
      </div>
    );
  }

  const isFinal = data.session?.status === 'completed';

  return (
    <div className="space-y-3" data-testid="standings-panel">
      <div className="flex items-baseline justify-between">
        <h3 className="font-serif text-2xl text-[#013523]">
          {isFinal ? 'Final standings' : 'Standings'}
        </h3>
        <div className="text-xs text-[#6a766e]">
          {data.session?.current_week} / {data.session?.weeks_planned} weeks played
        </div>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[#6a766e]">
              <th className="text-left py-2 px-2">#</th>
              <th className="text-left py-2 px-2">Player</th>
              <th className="text-right py-2 px-2">Pts</th>
              <th className="text-center py-2 px-2">Court</th>
              <th className="text-center py-2 px-2">Last</th>
              <th className="text-right py-2 px-2">Pl / Mi</th>
            </tr>
          </thead>
          <tbody>
            {data.standings.map((s, idx) => {
              const isMe = highlightProfileId && (s.profile_id === highlightProfileId);
              return (
              <tr
                key={s.id || s.registration_id}
                data-testid={`standings-row-${s.registration_id}`}
                className={`border-t border-[#e3dcd1] ${isMe ? 'bg-[#015e3f]/5' : ''}`}
              >
                <td className={`py-2 px-2 font-medium ${isMe ? 'text-[#015e3f]' : 'text-[#015e3f]'}`} data-testid={`standings-rank-${s.registration_id}`}>{idx + 1}</td>
                <td className="py-2 px-2 text-[#013523]">
                  {s.display_name || s.full_name || '—'}
                  {isMe && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#015e3f]">(you)</span>}
                </td>
                <td className="py-2 px-2 text-right font-medium text-[#013523]" data-testid={`standings-points-${s.registration_id}`}>{s.total_points}</td>
                <td className="py-2 px-2 text-center text-[#6a766e]">{s.current_court ?? '—'}</td>
                <td className="py-2 px-2 text-center">
                  {s.last_movement ? <MovementBadge movement={s.last_movement} label="" /> : <span className="text-[#6a766e]">—</span>}
                </td>
                <td className="py-2 px-2 text-right text-[11px] text-[#6a766e]">{s.weeks_played} / {s.weeks_missed}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
